import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { useWarehouseContext } from '../../../../../../contexts/WarehouseContext';
import { useHubWarehouseDspOptions } from '../../../../../../hooks/useHubWarehouseDspOptions';
import { SiteType } from '../../../../../../graphql/types/SiteType';
import { ARRIVAL_POINT_TYPE_HUB, DIMENSION_WHS } from '../../constants';
import {
  GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST,
  GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  DeliveryProgressMonitoringOfSlaPageRequestType,
  DeliveryProgressMonitoringOfSlaDetailPageRequestType,
  DeliveryProgressMonitoringOfSlaPageResponseType,
  DeliveryProgressMonitoringOfSlaDetailPageResponseType,
  DeliveryProgressMonitoringOfSlaSummaryType,
  DeliveryProgressMonitoringOfSlaType,
  DeliveryProgressMonitoringOfSlaDetailType,
  GetDeliveryProgressMonitoringOfSlaPageListResponse,
  GetDeliveryProgressMonitoringOfSlaDetailListResponse
} from '../queries/getDeliveryProgressMonitoringOfSlaList';
import {
  GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST,
  GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
  GetDspDeliveryProgressMonitoringOfSlaPageListResponse,
  GetDspDeliveryProgressMonitoringOfSlaDetailListResponse,
} from '../queries/getDspDeliveryProgressMonitoringOfSlaList';

/**
 * Hub 页面：获取当前 hub 下子仓库 ID 列表（用于 GetDeliveryProgressMonitoringOfSlaPageList 的 warehouseIds）
 */
function useHubWarehouseIds(): string[] {
  const { currentWarehouse } = useWarehouseContext();
  const isHub = currentWarehouse?.id != null && Number(currentWarehouse.siteType) === SiteType.HUB;
  const hubId = isHub ? Number(currentWarehouse!.id) : undefined;
  // hub 下子仓库改由后端结构关联查询返回（findByParentSiteId），替代旧权限接口客户端过滤
  const { warehouses } = useHubWarehouseDspOptions(hubId);
  return useMemo(() => {
    if (!currentWarehouse?.id) return [];
    if (!isHub) {
      return [String(currentWarehouse.id)];
    }
    return warehouses.map((w) => String(w.id));
  }, [currentWarehouse?.id, isHub, warehouses]);
}

/**
 * Hub 包裹 SLA 监控：分页列表查询（使用 GetDeliveryProgressMonitoringOfSlaPageList，dimension 为 dsp/whs）
 */
export const useHubDeliveryProgressMonitoringOfSlaQuery = (): {
  summaryData: DeliveryProgressMonitoringOfSlaSummaryType | null;
  recordData: DeliveryProgressMonitoringOfSlaType[];
  loading: boolean;
  error: any;
  refetch: any;
  handleFilter: (newPageRequest: DeliveryProgressMonitoringOfSlaPageRequestType) => void;
  currentFilters: DeliveryProgressMonitoringOfSlaPageRequestType | null;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
  hubWarehouseIds: string[];
} => {
  const [pageRequest, setPageRequest] = useState<DeliveryProgressMonitoringOfSlaPageRequestType | null>(null);
  const [shouldQuery, setShouldQuery] = useState(false);
  const hubWarehouseIds = useHubWarehouseIds();
  const { currentWarehouse } = useWarehouseContext();
  const hubId = currentWarehouse?.siteType != null && Number(currentWarehouse.siteType) === SiteType.HUB && currentWarehouse?.id
    ? Number(currentWarehouse.id)
    : undefined;

  const defaultPageRequest = useMemo((): DeliveryProgressMonitoringOfSlaPageRequestType | null => {
    if (hubId == null) return null;
    const today = new Date().toISOString().split('T')[0];
    return {
      filter: {
        hubId,
        arrivalDateStart: today,
        arrivalDateEnd: today,
        warehouseIds: undefined,
        dspIds: undefined,
        areaIds: undefined,
        cityNames: undefined,
        driverIds: undefined,
        arrivalPointType: ARRIVAL_POINT_TYPE_HUB,
        dimension: DIMENSION_WHS,
      },
      pageRequest: {
        page: 1,
        size: 20,
        sortBy: 'overallConversionRate',
        sortDirection: 'DESC'
      }
    };
  }, [hubId]);

  const effectivePageRequest = pageRequest || defaultPageRequest;

  const isDsp: boolean = false;

  const { data, loading, error, refetch } = useQuery<GetDeliveryProgressMonitoringOfSlaPageListResponse | GetDspDeliveryProgressMonitoringOfSlaPageListResponse>(
    isDsp
      ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST
      : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_PAGE_LIST,
    {
      variables: {
        pageRequest: effectivePageRequest
      },
      skip: !shouldQuery || !effectivePageRequest,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const slaPageList: DeliveryProgressMonitoringOfSlaPageResponseType | undefined = isDsp
    ? (data as GetDspDeliveryProgressMonitoringOfSlaPageListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaPageListForDsp
    : (data as GetDeliveryProgressMonitoringOfSlaPageListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaPageList;

  const handleFilter = (newPageRequest: DeliveryProgressMonitoringOfSlaPageRequestType) => {
    setPageRequest(newPageRequest);
    setShouldQuery(true);
  };

  return {
    summaryData: slaPageList?.summaryData || null,
    recordData: slaPageList?.pageResponse?.content || [],
    loading,
    error,
    refetch,
    handleFilter,
    currentFilters: pageRequest,
    pagination: slaPageList?.pageResponse ? {
      page: slaPageList.pageResponse.page,
      size: slaPageList.pageResponse.size,
      totalElements: slaPageList.pageResponse.totalElements,
      totalPages: slaPageList.pageResponse.totalPages,
      hasNext: slaPageList.pageResponse.hasNext,
      hasPrevious: slaPageList.pageResponse.hasPrevious,
    } : null,
    hubWarehouseIds,
  };
};

/**
 * Hub 包裹 SLA 监控：详情列表查询（与 warehouse 页面共用同一接口）
 */
export const useHubDeliveryProgressMonitoringOfSlaDetailQuery = (): {
  detailData: DeliveryProgressMonitoringOfSlaDetailType[];
  loading: boolean;
  error: any;
  refetch: any;
  handlePageRequest: (pageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType) => void;
  pagination: {
    page: number;
    size: number;
    totalElements: number;
    totalPages: number;
    hasNext: boolean;
    hasPrevious: boolean;
  } | null;
} => {
  const [pageRequest, setPageRequest] = useState<DeliveryProgressMonitoringOfSlaDetailPageRequestType | null>(null);
  const [shouldQuery, setShouldQuery] = useState(false);
  const { currentWarehouse } = useWarehouseContext();
  const warehouseId = currentWarehouse?.id ?? '';

  const isDsp: boolean = false;

  const { data, loading, error, refetch } = useQuery<GetDeliveryProgressMonitoringOfSlaDetailListResponse | GetDspDeliveryProgressMonitoringOfSlaDetailListResponse>(
    isDsp
      ? GET_DSP_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST
      : GET_DELIVERY_PROGRESS_MONITORING_OF_SLA_DETAIL_LIST,
    {
      variables: {
        pageRequest: pageRequest || {
          filter: {
            arrivalDateStart: new Date().toISOString().split('T')[0],
            arrivalDateEnd: new Date().toISOString().split('T')[0],
            warehouseIds: [Number(warehouseId)],
            dspIds: undefined,
            areaIds: undefined,
            cityNames: undefined,
            trackingNumber: undefined,
            statusField: undefined,
            arrivalPointType: 1,
          },
          page: 0,
          size: 10,
          sortBy: 'createdAt',
          sortDirection: 'DESC'
        }
      },
      skip: !shouldQuery || !pageRequest,
      errorPolicy: 'all',
      notifyOnNetworkStatusChange: true,
    }
  );

  const detailList: DeliveryProgressMonitoringOfSlaDetailPageResponseType | undefined = isDsp
    ? (data as GetDspDeliveryProgressMonitoringOfSlaDetailListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaDetailListForDsp
    : (data as GetDeliveryProgressMonitoringOfSlaDetailListResponse | undefined)?.getDeliveryProgressMonitoringOfSlaDetailList;

  const handlePageRequest = (newPageRequest: DeliveryProgressMonitoringOfSlaDetailPageRequestType) => {
    setPageRequest(newPageRequest);
    setShouldQuery(true);
  };

  return {
    detailData: detailList?.content || [],
    loading,
    error,
    refetch,
    handlePageRequest,
    pagination: detailList ? {
      page: detailList.page,
      size: detailList.size,
      totalElements: detailList.totalElements,
      totalPages: detailList.totalPages,
      hasNext: detailList.hasNext,
      hasPrevious: detailList.hasPrevious,
    } : null,
  };
};
